/**
 * @fileoverview Store Variants Admin API for debugging URL-to-Variant mappings.
 * @module features/debug/api/storeVariants
 */

import { z } from 'zod'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

// ============================================================================
// Schemas
// ============================================================================

/**
 * Store reference nested in store variant.
 */
export const storeRefSchema = z.object({
  id: z.number(),
  name: z.string(),
  platformType: z.string(),
  platformDomain: z.string(),
})
export type StoreRef = z.infer<typeof storeRefSchema>

/**
 * Variant reference nested in store variant.
 */
export const variantRefSchema = z.object({
  id: z.number(),
  productId: z.string(), // UUID string
  sku: z.string(),
  name: z.string(),
})
export type VariantRef = z.infer<typeof variantRefSchema>

/**
 * Store variant list item from backend StoreVariantListItemDTO.
 */
export const storeVariantSchema = z.object({
  id: z.number(),
  store: storeRefSchema,
  canonicalPath: z.string(),
  productSlug: z.string().nullable(),
  externalVariantId: z.string().nullable(),
  variant: variantRefSchema,
  source: z.string(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  createdBy: z.string().nullable(),
})
export type StoreVariant = z.infer<typeof storeVariantSchema>

/**
 * Page metadata from Spring Boot 3.x.
 */
const pageMetadataSchema = z.object({
  size: z.coerce.number().default(0),
  number: z.coerce.number().default(0),
  totalElements: z.coerce.number().default(0),
  totalPages: z.coerce.number().default(1),
})

/**
 * Paginated response from backend.
 */
export const paginatedStoreVariantsSchema = z
  .object({
    content: z.array(storeVariantSchema),
    page: pageMetadataSchema.optional(),
  })
  .passthrough()
  .transform(data => ({
    content: data.content,
    totalElements: data.page?.totalElements ?? 0,
    totalPages: data.page?.totalPages ?? 1,
    size: data.page?.size ?? 0,
    number: data.page?.number ?? 0,
    first: (data.page?.number ?? 0) === 0,
    last: (data.page?.number ?? 0) >= (data.page?.totalPages ?? 1) - 1,
    empty: data.content.length === 0,
  }))
export type PaginatedStoreVariants = z.infer<typeof paginatedStoreVariantsSchema>

/**
 * Count response from backend.
 */
export const countResponseSchema = z.object({
  total: z.number(),
  filters: z.object({
    storeId: z.union([z.number(), z.string()]),
    source: z.union([z.string(), z.literal('all')]),
  }),
})
export type CountResponse = z.infer<typeof countResponseSchema>

// ============================================================================
// Query Keys
// ============================================================================

export const STORE_VARIANTS_QUERY_KEY = ['admin', 'store-variants'] as const
export const STORE_VARIANTS_COUNT_QUERY_KEY = ['admin', 'store-variants', 'count'] as const

// ============================================================================
// Service
// ============================================================================

export interface ListStoreVariantsParams {
  storeId?: number | undefined
  source?: string | undefined
  variantId?: number | undefined
  search?: string | undefined
  page?: number | undefined
  size?: number | undefined
  sort?: string | undefined
}

export interface CountStoreVariantsParams {
  storeId?: number | undefined
  source?: string | undefined
}

export const storeVariantsService = {
  /**
   * Fetch paginated store variants with filters.
   */
  async list(params: ListStoreVariantsParams = {}): Promise<PaginatedStoreVariants> {
    const searchParams = new URLSearchParams()
    if (params.storeId !== undefined) {
      searchParams.set('storeId', String(params.storeId))
    }
    if (params.source) {
      searchParams.set('source', params.source)
    }
    if (params.variantId !== undefined) {
      searchParams.set('variantId', String(params.variantId))
    }
    if (params.search) {
      searchParams.set('search', params.search)
    }
    if (params.page !== undefined) {
      searchParams.set('page', String(params.page))
    }
    if (params.size !== undefined) {
      searchParams.set('size', String(params.size))
    }
    if (params.sort) {
      searchParams.set('sort', params.sort)
    }

    const url = `/admin/store-variants${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
    const response = await apiClient.get(url)
    return paginatedStoreVariantsSchema.parse(response.data)
  },

  /**
   * Get count of store variants with optional filtering.
   */
  async count(params: CountStoreVariantsParams = {}): Promise<CountResponse> {
    const searchParams = new URLSearchParams()
    if (params.storeId !== undefined) {
      searchParams.set('storeId', String(params.storeId))
    }
    if (params.source) {
      searchParams.set('source', params.source)
    }

    const url = `/admin/store-variants/count${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
    const response = await apiClient.get(url)
    return countResponseSchema.parse(response.data)
  },
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetches paginated store variants for debugging.
 */
export function useStoreVariantsQuery(
  params: ListStoreVariantsParams = {},
  options?: { enabled?: boolean }
): UseQueryResult<PaginatedStoreVariants, Error> {
  return useQuery<PaginatedStoreVariants, Error>({
    queryKey: [...STORE_VARIANTS_QUERY_KEY, params],
    queryFn: () => storeVariantsService.list(params),
    staleTime: 30 * 1000, // 30 seconds for debug data
    enabled: options?.enabled ?? true,
  })
}

/**
 * Fetches count of store variants.
 */
export function useStoreVariantsCountQuery(
  params: CountStoreVariantsParams = {},
  options?: { enabled?: boolean }
): UseQueryResult<CountResponse, Error> {
  return useQuery<CountResponse, Error>({
    queryKey: [...STORE_VARIANTS_COUNT_QUERY_KEY, params],
    queryFn: () => storeVariantsService.count(params),
    staleTime: 30 * 1000,
    enabled: options?.enabled ?? true,
  })
}

export default storeVariantsService
